import React, { useCallback, useEffect, useState } from 'react';
import { LuPercent } from 'react-icons/lu';

import type { PercentInputProps } from './types';
import { filterPercentInput, formatPercent, parsePercent } from './util';
import { Label } from '../../general/Label';
import { cn } from '../../utils/cn';

export type { PercentInputProps } from './types';

/**
 * A form percentage input component with percent icon suffix.
 *
 * @param id - ID of the input element. Used for htmlFor association.
 * @param value - The current percentage value.
 * @param onChange - Callback fired when the value changes.
 * @param label - Label text for the input.
 * @param layout - Layout direction: "horizontal" (default) or "vertical".
 * @param error - Error message to display below the input.
 * @param min - Minimum allowed value (default: 0).
 * @param max - Maximum allowed value (default: 100).
 * @param allowDecimals - Whether to allow decimal numbers (default: true).
 * @param placeholder - Placeholder text for the input.
 * @param disabled - Whether the input is disabled.
 * @param className - Additional CSS classes for the container.
 * @param labelClassName - Additional CSS classes for the label.
 * @param inputClassName - Additional CSS classes for the input wrapper.
 * @param ...props - Additional HTML input attributes.
 *
 * @example
 * // Basic usage
 * <PercentInput label="Anteil" id="share" value={share} onChange={setShare} />
 *
 * @example
 * // With custom range
 * <PercentInput label="Rate" id="rate" min={0} max={200} value={rate} onChange={setRate} />
 *
 * @example
 * // Integer only
 * <PercentInput label="Prozent" id="percent" allowDecimals={false} value={pct} onChange={setPct} />
 *
 * @example
 * // With error state
 * <PercentInput label="Anteil" id="share" value={share} onChange={setShare} error="Ungültiger Prozentsatz" />
 */
export const PercentInput: React.FC<PercentInputProps> = ({
    allowDecimals = true,
    className,
    disabled,
    error,
    id,
    inputClassName,
    label,
    labelClassName,
    layout = 'horizontal',
    max = 100,
    min = 0,
    onChange,
    placeholder = '0',
    value,
    ...props
}) => {
    const isHorizontal = layout === 'horizontal';
    const hasError = Boolean(error);

    // Internal state for the displayed value (allows typing without immediate formatting)
    const [displayValue, setDisplayValue] = useState<string>('');

    // Sync display value with external value
    useEffect(() => {
        setDisplayValue(formatPercent(value, allowDecimals));
    }, [value, allowDecimals]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const rawValue = e.target.value;

            // Allow empty input
            if (rawValue === '') {
                setDisplayValue('');
                onChange?.(null);
                return;
            }

            // Filter invalid characters but don't format yet
            const filteredValue = filterPercentInput(rawValue, allowDecimals);
            setDisplayValue(filteredValue);

            // Parse and notify parent (may be invalid during typing)
            const numericValue = parsePercent(filteredValue);
            if (numericValue !== null) {
                onChange?.(numericValue);
            }
        },
        [onChange, allowDecimals],
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            // Parse the current display value
            let numericValue = parsePercent(displayValue);

            if (numericValue !== null) {
                // Apply min constraint
                if (numericValue < min) {
                    numericValue = min;
                }

                // Apply max constraint
                if (numericValue > max) {
                    numericValue = max;
                }

                // Format and update display
                setDisplayValue(formatPercent(numericValue, allowDecimals));
                onChange?.(numericValue);
            } else if (displayValue === '') {
                onChange?.(null);
            }

            // Call original onBlur if provided
            props.onBlur?.(e);
        },
        [displayValue, min, max, allowDecimals, onChange, props],
    );

    return (
        <div
            className={cn(
                'flex',
                isHorizontal ? 'flex-row gap-2.5 items-start' : 'flex-col gap-2 items-start',
                className,
            )}
        >
            {label && (
                <Label
                    htmlFor={id}
                    className={cn(
                        isHorizontal ? 'w-[125px] shrink-0 justify-end h-8' : 'w-full px-0 py-0 justify-start',
                        labelClassName,
                    )}
                >
                    {label}
                </Label>
            )}
            <div className={cn('flex flex-col', isHorizontal ? 'flex-1' : 'w-full')}>
                <div
                    className={cn(
                        'h-[32px] w-full',
                        'flex items-center gap-2',
                        'pl-3',
                        'bg-white border rounded-[4px]',
                        'font-normal font-roboto leading-[1.46]',
                        hasError ? 'border-alert-error' : 'border-grey-dark',
                        'focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-focus focus-within:ring-offset-1',
                        disabled && 'cursor-not-allowed bg-surface-light',
                        inputClassName,
                    )}
                    data-testid="percent-input-wrapper"
                >
                    <input
                        id={id}
                        type="text"
                        inputMode={allowDecimals ? 'decimal' : 'numeric'}
                        disabled={disabled}
                        value={displayValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={placeholder}
                        className={cn(
                            'flex-1 min-w-0 h-full',
                            'bg-transparent border-none outline-none',
                            'text-sm text-midnight',
                            'placeholder:text-midnight-placeholder',
                            'disabled:cursor-not-allowed disabled:bg-surface-light disabled:opacity-50',
                        )}
                        {...props}
                    />
                    <div className="flex items-center ml-auto shrink-0 h-full">
                        <span className="border-l border-grey-dark h-4/6" aria-hidden="true" />
                        <span
                            className={cn(
                                'flex items-center justify-center h-5 w-8 shrink-0 text-midnight select-none',
                                disabled && 'opacity-50',
                            )}
                            aria-hidden="true"
                            data-testid="percent-input-icon"
                        >
                            <LuPercent className="size-5" />
                        </span>
                    </div>
                </div>
                {hasError && <span className="text-[10px] leading-[1.46] text-alert-error mt-1">{error}</span>}
            </div>
        </div>
    );
};
